import store from '../stores/store'

class TokenRefreshManager {
	constructor() {
		this.refreshInProgress = false
		this.refreshPromise = null
	}

	// Check if token needs refresh (within 2 minutes of expiration)
	shouldRefreshToken(user) {
		if (!user?.kc_token) return false
		
		// Don't attempt refresh if refresh token itself is expired/expiring
		if (!this.isRefreshTokenValid(user)) {
			console.log('Refresh token is expired or expiring soon, not attempting refresh')
			return false
		}
		
		const decoded = this.decodeToken(user.kc_token)
		if (!decoded?.exp) return false
		
		const expiresAt = decoded.exp * 1000
		const now = Date.now()
		const timeUntilExpiry = expiresAt - now
		
		// Refresh if less than 2 minutes remaining
		return timeUntilExpiry < (2 * 60 * 1000)
	}

	decodeToken(token) {
		try {
			return JSON.parse(atob(token.split('.')[1]))
		}
		catch (e) {
			console.error('Failed to decode token:', e)
			return null
		}
	}

	// Check if refresh token is still valid
	isRefreshTokenValid(user) {
		if (!user?.kc_refresh_token) return false
		
		const decoded = this.decodeToken(user.kc_refresh_token)
		if (!decoded?.exp) return false
		
		const expiresAt = decoded.exp * 1000
		const now = Date.now()
		
		// Refresh token is valid if it has more than 30 seconds remaining
		return (expiresAt - now) > 30000
	}

	// Get time until refresh token expires (in seconds)
	getRefreshTokenExpiresInSeconds(user) {
		if (!user?.kc_refresh_token) return 0
		
		const decoded = this.decodeToken(user.kc_refresh_token)
		if (!decoded?.exp) return 0
		
		const expiresAt = decoded.exp * 1000
		const now = Date.now()
		
		return Math.max(0, Math.floor((expiresAt - now) / 1000))
	}

	// Refresh the token
	async refreshToken() {
		// Prevent multiple simultaneous refresh attempts
		if (this.refreshInProgress) {
			return this.refreshPromise
		}

		const user = store.state.loggedInUser
		if (!user?.kc_refresh_token) {
			throw new Error('No refresh token available')
		}

		// Check if refresh token is still valid before attempting refresh
		if (!this.isRefreshTokenValid(user)) {
			console.log('Refresh token expired, cannot refresh')
			throw new Error('Refresh token expired')
		}

		this.refreshInProgress = true
		
		this.refreshPromise = fetch('/api/refresh-token', {
			method: 'POST',
			headers: {
				'Content-Type': 'application/json',
			},
			body: JSON.stringify({
				refresh_token: user.kc_refresh_token
			})
		})
		.then(async response => {
			if (!response.ok) {
				throw new Error('Token refresh failed')
			}
			
			const updatedUser = await response.json()
			await store.commit('setLoggedInUser', updatedUser)
			
			// Schedule next refresh only if successful
			this.scheduleTokenRefresh(updatedUser)
			
			console.log('Token refreshed successfully')
			return updatedUser
		})
		.catch(error => {
			console.error('Token refresh error:', error)
			// Don't schedule another refresh on failure
			// Clear the timer to stop the refresh cycle
			store.commit('clearRefreshTimer')
			
			// Check if we should trigger logout (refresh token expired)
			const currentUser = store.state.loggedInUser
			if (!this.isRefreshTokenValid(currentUser)) {
				console.log('Refresh token expired after failed refresh attempt')
				// We can't directly trigger logout here, but the tokenHasExpired watcher should catch it
			}
			
			throw error
		})
		.finally(() => {
			this.refreshInProgress = false
			this.refreshPromise = null
		})

		return this.refreshPromise
	}

	// Schedule proactive token refresh
	scheduleTokenRefresh(user) {
		// Clear existing timer
		store.commit('clearRefreshTimer')
		
		if (!user?.kc_token) return
		
		// Don't schedule refresh if refresh token is invalid
		if (!this.isRefreshTokenValid(user)) {
			console.log('Refresh token expired, not scheduling refresh')
			return
		}
		
		const accessDecoded = this.decodeToken(user.kc_token)
		const refreshDecoded = this.decodeToken(user.kc_refresh_token)
		
		if (!accessDecoded?.exp || !refreshDecoded?.exp) return
		
		const accessExpiresAt = accessDecoded.exp * 1000
		const refreshExpiresAt = refreshDecoded.exp * 1000
		const now = Date.now()
		
		// Calculate time until access token expires
		const timeUntilAccessExpiry = accessExpiresAt - now
		
		// Calculate time until refresh token expires
		const timeUntilRefreshExpiry = refreshExpiresAt - now
		
		// If refresh token expires within 2 minutes of access token expiry,
		// this is the "last" token - don't schedule another refresh
		const timeDifference = refreshExpiresAt - accessExpiresAt
		if (timeDifference < (2 * 60 * 1000)) {
			console.log('This is the last token (refresh token expires soon), not scheduling refresh')
			return
		}
		
		// Schedule refresh for 2 minutes before access token expiry
		const refreshTime = Math.max(0, timeUntilAccessExpiry - (2 * 60 * 1000))
		
		// Don't schedule if refresh time is immediate (< 5 seconds) - likely means we're on last token
		if (refreshTime < 5000) {
			console.log('Refresh time too short, not scheduling (likely last token)')
			return
		}
		
		console.log(`Scheduling token refresh in ${Math.floor(refreshTime / 1000)} seconds`)
		
		const timer = setTimeout(() => {
			this.refreshToken().catch(err => {
				console.error('Scheduled token refresh failed:', err)
			})
		}, refreshTime)
		
		store.state.refreshTokenTimer = timer
	}

	// Initialize refresh scheduling on app load
	initialize() {
		const user = store.state.loggedInUser
		if (user?.kc_token) {
			this.scheduleTokenRefresh(user)
		}
	}
}

export const tokenRefreshManager = new TokenRefreshManager()
export default tokenRefreshManager

